import React, {ReactElement} from "react"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSectionTable} from "../../containers/MainSection/MainSection"
import Payments from "../../components/Payments/Payments"
import {useQueryState} from "use-location-state"
import {useAccessToken, useIsBankUser, useIsUnitUser, useUserClaimsData} from "../../services/auth"
import {hasPermission} from "../../services/permission"
import {startCase} from "lodash"
import {AssigneeType, AssignmentResourceType, AssignmentStage} from "../../resources/assignments"
import {TabsMeta, TitleBarTabs} from "../../components/TitleBarTabs/TitleBarTabs"
import {AssignmentsHub, AssignmentsHubColumns} from "../AssignmentsHub/AssignmentsHub"
import {isProdEnv, isSandboxOrLocalEnv} from "../../utilities/environment"
import {AssignmentsAmountIndicator} from "../../components/AssignmentAmountIndicator/AssignmentsAmountIndicator"
import {PaymentColumns} from "../../components/Payments/Payments.types"

enum PaymentsTabs {
	All = "All",
	Assignments = "Assignments",
}

export default function PaymentsPage(): ReactElement {
	const [currentTab, setCurrentTab] = useQueryState<PaymentsTabs>("tab", PaymentsTabs.All)
	const accessToken = useAccessToken()
	const claims = useUserClaimsData()
	const assigneeType = startCase(claims.userType) as AssigneeType
	const liClassName = "assignments-tab-li" as const
	const commonTabProps = {liClassName}
	const tabs: TabsMeta<PaymentsTabs>[] = [
		{label: "All", key: PaymentsTabs.All},
		{
			label: "Assignments",
			key: PaymentsTabs.Assignments,
			suffixElement: (
				<AssignmentsAmountIndicator
					findAssignmentsArgs={{
						accessToken,
						assignees: [assigneeType],
						stages: [AssignmentStage.Pending],
						limit: 0,
						resourceTypes: [
							AssignmentResourceType.DrawdownOutgoingWire,
							AssignmentResourceType.OutgoingWire,
							AssignmentResourceType.IncomingWire,
						],
					}}
					isActive={currentTab == PaymentsTabs.Assignments}
					tooltip={
						<>
							Pending assignment
							<br /> for {assigneeType} user
						</>
					}
				/>
			),
		},
	].map((tab) => ({...tab, ...commonTabProps}))
	const paymentsProps = {
		enableDirectionFilter: true,
		fullHeight: true,
		enableOrgFilter: true,
		enableBankFilter: true,
		enableStatusFilter: true,
		disableDrawer: useIsUnitUser() || useIsBankUser(),
		enableTypeFilter: true,
		enableStatusPhasesFilter: true,
		enableAmountFilter: true,
		enableFeaturesFilter: true,
		includedColumns: [
			PaymentColumns.id,
			...(useIsUnitUser() ? [PaymentColumns.org] : []),
			...(useIsUnitUser() ? [PaymentColumns.bank] : []),
			PaymentColumns.type,
			PaymentColumns.accountId,
			PaymentColumns.customer,
			PaymentColumns.amount,
			PaymentColumns.status,
			PaymentColumns.createdAt,
			PaymentColumns.expectedCompletionDate,
			...(hasPermission("payment.achDebit.clear", "create") ? [PaymentColumns.actions] : []),
		],
	}
	const isUnitUser = useIsUnitUser()
	const isBankUser = useIsBankUser()
	const isBankOrUnitUser = isUnitUser || isBankUser
	const sandboxAndLocalOrgIds = [
		"3475",
		"2654",
		"5297",
		"5314",
		"5326",
		"5328",
		"5329",
		"5335",
		"5336",
		"2673" /*TamirS Test Org (dev)*/,
		"1221" /*Relay org*/,
		"27" /*Covercy sandbox org*/,
	]
	const sandboxAndLocalBankIds = ["20", "26"]
	const showAssignmentsOnSandBox =
		isSandboxOrLocalEnv() &&
		(sandboxAndLocalOrgIds.includes(claims.orgId) || sandboxAndLocalBankIds.includes(claims.bankId) || isUnitUser) //TODO: Remove once going live
	const showAssignmentsOnProd = isProdEnv()
	const showAssignments = showAssignmentsOnSandBox || showAssignmentsOnProd
	return (
		<>
			<TitleBar title="Payments" noBorder={showAssignments} />
			{showAssignments && (
				<TitleBarTabs
					currentTab={currentTab}
					setCurrentTab={setCurrentTab}
					tabs={tabs}
					navClassName="assignments-tab-nav"
				/>
			)}
			<MainSectionTable>
				{showAssignments ? (
					<>
						{currentTab == PaymentsTabs.All && <Payments {...paymentsProps} showTabsOnHeader />}
						{currentTab == PaymentsTabs.Assignments && (
							<AssignmentsHub
								token={accessToken}
								includedColumns={[
									AssignmentsHubColumns.isOwner,
									AssignmentsHubColumns.resourceType,
									AssignmentsHubColumns.resourceId,
									...(isBankOrUnitUser ? [AssignmentsHubColumns.org] : []),
									...(isUnitUser ? [AssignmentsHubColumns.bank] : []),
									AssignmentsHubColumns.customer,
									AssignmentsHubColumns.amount,
									AssignmentsHubColumns.assignmentId,
									AssignmentsHubColumns.assignmentType,
									AssignmentsHubColumns.assignee,
									AssignmentsHubColumns.assignmentStatus,
									AssignmentsHubColumns.createdAt,
								]}
								fullHeight
								enableResourceTypeFilter
								enableAssigneeFilter
								enableAssignmentStageFilter
								enableAssignmentTypeFilter
								enableOrgFilter
								enableBankFilter
								enableAmountFilter
								resourceTypes={[
									AssignmentResourceType.DrawdownOutgoingWire,
									AssignmentResourceType.OutgoingWire,
									AssignmentResourceType.IncomingWire,
								]}
							/>
						)}
					</>
				) : (
					<Payments {...paymentsProps} />
				)}
			</MainSectionTable>
		</>
	)
}
